'use client';

// Textarea with @mention autocomplete. Two modes:
//  - `users` prop given (e.g. ChatRoomView on /live, scoped to the Zajednica roster): filters
//    that fixed list client-side, instantly, from the first "@" — no minimum length, like any
//    chat app's mention picker.
//  - `users` omitted (forum's default): searches the whole site via /api/search, debounced,
//    2-char minimum (unchanged — a sitewide fetch on every keystroke would be wasteful).
// Selecting inserts @username at the caret. Drop-in replacement for the forum's plain textareas.

import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState, type TextareaHTMLAttributes } from 'react';

interface MentionUser { id: string; username: string | null; image: string | null }

type Props = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  value: string;
  onValueChange: (v: string) => void;
  /** Fixed list to filter locally instead of querying /api/search (e.g. Zajednica roster). */
  users?: MentionUser[];
};

// The "@word" being typed at the caret (query may be empty, i.e. just typed "@"), or null.
function activeMention(value: string, caret: number): { start: number; query: string } | null {
  const upto = value.slice(0, caret);
  const m = upto.match(/(^|[\s(])@([A-Za-z0-9_.]{0,30})$/);
  if (!m) return null;
  return { start: upto.length - m[2].length - 1, query: m[2] };
}

export default forwardRef<HTMLTextAreaElement, Props>(function MentionTextarea({ value, onValueChange, users: fixedUsers, ...rest }, forwardedRef) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(forwardedRef, () => taRef.current as HTMLTextAreaElement, []);
  const [fetchedUsers, setFetchedUsers] = useState<MentionUser[]>([]);
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [highlight, setHighlight] = useState(0);
  const fetchSeq = useRef(0);

  useEffect(() => {
    if (fixedUsers) return; // local-filter mode — nothing to fetch
    if (!mention || mention.query.length < 2) { setFetchedUsers([]); return; }
    const seq = ++fetchSeq.current;
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(mention.query)}`);
        const d = await res.json();
        if (seq !== fetchSeq.current) return; // stale response
        const list = ((d.users ?? []) as MentionUser[]).filter((u) => u.username).slice(0, 5);
        setFetchedUsers(list);
        setHighlight(0);
      } catch {
        if (seq === fetchSeq.current) setFetchedUsers([]);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [fixedUsers, mention?.query, mention?.start]); // eslint-disable-line react-hooks/exhaustive-deps

  const users = useMemo(() => {
    if (!fixedUsers) return fetchedUsers;
    if (!mention) return [];
    const q = mention.query.toLowerCase();
    return fixedUsers.filter((u) => u.username && u.username.toLowerCase().startsWith(q)).slice(0, 8);
  }, [fixedUsers, fetchedUsers, mention]);
  useEffect(() => { setHighlight(0); }, [users.length]);

  function syncMention(nextValue: string) {
    const caret = taRef.current?.selectionStart ?? nextValue.length;
    setMention(activeMention(nextValue, caret));
  }

  function pick(username: string) {
    if (!mention) return;
    const caret = taRef.current?.selectionStart ?? value.length;
    const next = value.slice(0, mention.start) + '@' + username + ' ' + value.slice(caret);
    onValueChange(next);
    setMention(null);
    setFetchedUsers([]);
    // Restore focus + caret after the inserted mention
    requestAnimationFrame(() => {
      const ta = taRef.current;
      if (ta) {
        ta.focus();
        const pos = mention.start + username.length + 2;
        ta.setSelectionRange(pos, pos);
      }
    });
  }

  const open = mention !== null && users.length > 0;

  return (
    <div className="relative">
      <textarea
        {...rest}
        ref={taRef}
        value={value}
        onChange={(e) => { onValueChange(e.target.value); syncMention(e.target.value); }}
        onKeyDown={(e) => {
          if (open) {
            if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => (h + 1) % users.length); return; }
            if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => (h - 1 + users.length) % users.length); return; }
            if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(users[highlight].username!); return; }
            if (e.key === 'Escape') { setMention(null); setFetchedUsers([]); return; }
          }
          rest.onKeyDown?.(e);
        }}
        onClick={(e) => { syncMention(value); (rest.onClick as any)?.(e); }}
        onBlur={(e) => {
          // Delay so a click on a suggestion registers before the list closes
          setTimeout(() => { setMention(null); setFetchedUsers([]); }, 150);
          rest.onBlur?.(e);
        }}
      />
      {open && (
        <div
          className="absolute z-[65] left-2 right-2 rounded-xl py-1 animate-fade-in"
          style={{ top: '100%', marginTop: -6, background: '#1a2030', border: '1px solid #2c3547', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}
        >
          {users.map((u, i) => (
            <button
              key={u.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(u.username!); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left"
              style={{ background: i === highlight ? 'rgba(99,102,241,0.12)' : 'transparent', color: '#cbd5e1' }}
              onMouseEnter={() => setHighlight(i)}
            >
              {u.image ? (
                <img src={u.image} alt="" className="rounded-full" style={{ width: 18, height: 18 }} />
              ) : (
                <span className="rounded-full" style={{ width: 18, height: 18, background: '#252d3d' }} />
              )}
              <span className="font-medium">@{u.username}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});
